import { useState } from 'react'
import { useUserSettings } from 'src/Hooks'
import { useTypedSelector } from 'src/State/RootReducer'
import { getPvForecastWidgetChartData, getPvForecastWidgetValueAndUnit } from '../utils/pv-forecast-widget'
import { date } from '@e3dc-react/shell/Libs/date'
import { PVForecastReturnType, usePVForecastData } from 'src/Libs/system-data-handler'
import { PvForecastWidgetData } from '../types'

/*
 * A hook that fetches the data for the pv forecast widget.
 */
const useDataForPvForecastWidget = (): PvForecastWidgetData => {
  const { isUserSettingsLoading, userSettings, updateUserSettings } = useUserSettings()
  const isStorageLoading = useTypedSelector(state => state.storage.storageLoading)
  const { pvForecastData: forecastPowerData, isLoading } = useForecastPowerDataForPvForecastWidget()

  const [showRemainingProduction, setShowRemainingProduction] = useState(userSettings?.showRemainingProductionInForecastWidget || false)
  const pvForecastWidgetChartData = getPvForecastWidgetChartData(forecastPowerData, showRemainingProduction)
  const pvForecastWidgetValueAndUnit = getPvForecastWidgetValueAndUnit(forecastPowerData, showRemainingProduction)

  const isNoDataAvailable = forecastPowerData.length === 0
  const isPvForecastWidgetDataLoading = isStorageLoading || isUserSettingsLoading || isLoading

  const handleToggleShowRemainingProduction = (): void => {
    setShowRemainingProduction(prev => !prev)
    updateUserSettings({ showRemainingProductionInForecastWidget: !showRemainingProduction })
  }

  return {
    showRemainingProduction,
    handleToggleShowRemainingProduction,
    pvForecastWidgetChartData,
    pvForecastWidgetValueAndUnit,
    isNoDataAvailable,
    isPvForecastWidgetDataLoading,
  }
}

export default useDataForPvForecastWidget

/* Fetches 24 hour forecast power data for the widget */
const useForecastPowerDataForPvForecastWidget = (): PVForecastReturnType => {
  const from = date.startOfDay(new Date())
  const to = date.endOfDay(new Date())
  const storageId = useTypedSelector(state => state.storage.activeStorage)

  const { pvForecastData, isLoading } = usePVForecastData(from, to, storageId)
  const forecastPowerData = pvForecastData.map(fp => ({ ...fp, power: fp.power ?? 0 }))
  return { pvForecastData: forecastPowerData, isLoading }
}
